/**
 * Root-only auth side effects: verify Etsy connection + sync browser timezone.
 *
 * These two effects USED TO live in ``useAuth``, but 28 components in the
 * app call ``useAuth()`` and each mount fired its own effect. On every page
 * load that meant ~7 parallel ``/third-party/verify-connection`` round-trips
 * and ~7 ``PATCH /users/me/timezone`` writes within the first second — each
 * verify potentially fanning out into Etsy ``/users/me`` calls, burning
 * the shared daily quota for nothing.
 *
 * The hook is mounted ONCE in ``AppLayout`` so its effects fire at most
 * once per state change. ``useAuth`` is now a pure state-access hook.
 *
 * Module-level promise singletons add a second line of defense: even if
 * something accidentally re-introduces a duplicate mount, concurrent
 * callers share the in-flight request instead of fanning out.
 */
import { useEffect } from 'react';

import API_ENDPOINTS from '../constants/apiEndpoints';
import authService from '../services/authService';
import useAuthStore from '../stores/authStore';

// Detect the browser's IANA timezone (e.g. "America/Los_Angeles") so the server
// can render dates that match what the seller sees in third-party dashboards.
// Returns null if the browser doesn't support Intl.DateTimeFormat.
const detectBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

// 5 min — matches the backend's verify-connection cache TTL.
const VERIFY_SKIP_WINDOW_MS = 5 * 60 * 1000;

// Module-level promise singleton for the timezone PATCH. ``checkEtsyConnection``
// has its own dedup inside ``authService``; this one lives here because the
// PATCH is a one-liner directly against the endpoint.
let _timezonePatchInFlight = null;
// Track the last timezone value we successfully PATCHed so we don't retry
// the same value on every mount even if the local user object hasn't been
// re-fetched. Persists for the page lifetime only.
let _lastSyncedTimezone = null;

const syncTimezone = async tz => {
  if (_timezonePatchInFlight) {
    return _timezonePatchInFlight;
  }
  _timezonePatchInFlight = (async () => {
    try {
      // makeAuthenticatedRequest throws on non-2xx and returns the parsed
      // JSON body on success — so reaching the next line means the PATCH
      // committed.
      await authService.makeAuthenticatedRequest(API_ENDPOINTS.AUTH.UPDATE_TIMEZONE, {
        method: 'PATCH',
        body: JSON.stringify({ timezone: tz }),
      });
      _lastSyncedTimezone = tz;
      // Update the local user object so the dep array stabilizes — without
      // this, ``user?.timezone`` stays null on subsequent renders and the
      // effect fires again. Pulling state freshly via ``getState`` to avoid
      // depending on a stale closure.
      const { user, userToken, setUserAuth } = useAuthStore.getState();
      if (user && userToken && user.timezone !== tz) {
        setUserAuth({ ...user, timezone: tz }, userToken);
      }
    } catch (err) {
      // eslint-disable-next-line no-console -- non-fatal but worth surfacing during diagnosis
      console.warn('Timezone sync failed:', err?.message || err);
    } finally {
      _timezonePatchInFlight = null;
    }
  })();
  return _timezonePatchInFlight;
};

const useAuthBootstrap = () => {
  const isUserAuthenticated = useAuthStore(s => s.isUserAuthenticated);
  const userToken = useAuthStore(s => s.userToken);
  const userTimezone = useAuthStore(s => s.user?.timezone);
  // Only re-verify when the actual access token flips. Subscribing to the
  // whole etsyTokens object would re-fire on every token shape change.
  const etsyAccessToken = useAuthStore(s => s.etsyTokens.accessToken);

  // Verify Etsy connection on auth state changes.
  //
  // Skip rules:
  //   - last verify happened < VERIFY_SKIP_WINDOW_MS ago, AND
  //   - we currently believe we're connected.
  // First-time login and post-disconnect reconnect still verify because
  // etsyLastVerifiedAt is null in those cases.
  useEffect(() => {
    if (!isUserAuthenticated || !userToken) return;
    const lastVerifiedAt = useAuthStore.getState().etsyLastVerifiedAt;
    const isCurrentlyConnected = useAuthStore.getState().isEtsyConnected;
    const fresh = lastVerifiedAt && Date.now() - lastVerifiedAt < VERIFY_SKIP_WINDOW_MS;
    if (fresh && isCurrentlyConnected) {
      return;
    }
    authService.checkEtsyConnection();
  }, [isUserAuthenticated, userToken, etsyAccessToken]);

  // Sync the browser's timezone to the backend on auth so server-rendered
  // dates (packing slips, etc.) match what the seller sees elsewhere.
  useEffect(() => {
    if (!isUserAuthenticated || !userToken) return;
    const tz = detectBrowserTimezone();
    if (!tz) return;
    // Three short-circuits, each cheap:
    //   1. Already matches local user state — nothing to do.
    //   2. Already PATCHed this value this page lifetime.
    //   3. A PATCH is in flight; the singleton handles it.
    if (tz === userTimezone) return;
    if (tz === _lastSyncedTimezone) return;
    syncTimezone(tz);
  }, [isUserAuthenticated, userToken, userTimezone]);
};

export default useAuthBootstrap;
